import React, { useContext } from 'react';
import { styled } from '@compiled/react';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import ReactRenderer from '@atlaskit/renderer/renderer';
import type ProviderFactory from '@atlaskit/editor-common/provider-factory/provider-factory';

import { TEMPLATE_PREVIEW_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getSmartCardRenderers } from '@confluence/fabric-extension-lib/entry-points/getSmartCardRenderers.ts';
import { getMediaFeatureFlags } from '@confluence/fabric-media-support/entry-points/mediaFeatureFlags.ts';
import { RendererAnalyticsListener } from '@confluence/fabric-analytics-listeners/entry-points/RendererAnalyticsListener.ts';
import { usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { ConfluenceSmartCardProvider } from '@confluence/smart-card-provider/entry-points/ConfluenceSmartCardProvider.tsx';

import { NoPreviewAvailable } from '../PreviewMessage/index.ts';
import { PreviewAnalyticsContext } from '../PreviewAnalytics/index.ts';
import type { TemplatePreviewTemplate } from '../templatePreviewHelpers.tsx';

import { getExtensionHandlers } from './extensions/index.ts';

type StylingProps = {
	allowTableResizing?: boolean;
};

type Props = StylingProps & {
	template?: TemplatePreviewTemplate;
	previewString: string;
	fabricDataProviders?: ProviderFactory;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FixTableHorizontalScrollbars = styled.div<StylingProps>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		!props.allowTableResizing &&
		`
  .pm-table-container {
    width: auto !important;
    left: auto !important;

    colgroup {
      display: none !important;
    }
  }
    `,
);

export const AdfPreviewRenderer = ({
	template,
	allowTableResizing = false,
	previewString,
	fabricDataProviders,
}: Props): React.JSX.Element => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { featureFlags, cloudId, userId } = useSessionData();

	const { fireAnalyticsEventWithTemplateData } = useContext(PreviewAnalyticsContext);
	const [contentId] = usePageContentId();
	const [spaceKey] = usePageSpaceKey();
	let adf;

	try {
		adf = JSON.parse(previewString);
	} catch (e) {
		fireAnalyticsEventWithTemplateData({
			type: 'sendTrackEvent',
			data: {
				source: 'templateDrawer',
				action: 'error',
				actionSubject: 'templatePreview',
				actionSubjectId: 'templatePreviewAdfParsingError',
				attributes: {
					errorMessage: e.message,
				},
			},
		});

		return <NoPreviewAvailable error={e} />;
	}

	const experienceSuccess = () =>
		experienceTracker.succeed({
			name: TEMPLATE_PREVIEW_EXPERIENCE,
		});

	const experienceFailure = (error: ApolloErrorInterop | Error) =>
		experienceTracker.fail({
			name: TEMPLATE_PREVIEW_EXPERIENCE,
			error,
		});

	const extensionHandlers = getExtensionHandlers({
		adf,
		cloudId,
		userId,
		contentId,
		spaceKey,
		template,
	});

	const content = (
		<RendererAnalyticsListener source="templatePreview">
			<ReactRenderer
				adfStage="stage0"
				document={adf}
				appearance="full-page"
				allowColumnSorting={false}
				enableSsrInlineScripts={false}
				allowHeadingAnchorLinks={false}
				stickyHeaders={undefined}
				dataProviders={fabricDataProviders}
				extensionHandlers={extensionHandlers}
				key={previewString}
				onComplete={experienceSuccess}
				onError={experienceFailure}
				allowPlaceholderText
				media={{
					featureFlags: getMediaFeatureFlags(featureFlags),
				}}
			/>
		</RendererAnalyticsListener>
	);

	return (
		<FixTableHorizontalScrollbars allowTableResizing={allowTableResizing}>
			<ConfluenceSmartCardProvider {...getSmartCardRenderers()}>
				{content}
			</ConfluenceSmartCardProvider>
		</FixTableHorizontalScrollbars>
	);
};
